/**
 * The shape of a Vibe deployment's customer-visible `errorReason`, and the one
 * rule that makes it safe to read.
 *
 * A failure reason has two halves with two different authors:
 *
 *   <the platform's named reason>        ← written by the control plane
 *                                          (which alone knows the deployment's
 *                                          lifecycle position, and therefore
 *                                          what the failure MEANS)
 *   <separator>
 *   <diagnostics>                        ← what the failed allocation could say
 *                                          for itself, relayed verbatim by the
 *                                          tenant agent: what the container
 *                                          runtime reported, the exit code, and
 *                                          the tail of the app's stderr — each
 *                                          present only when it exists, and
 *                                          absent entirely for a failure with
 *                                          nothing to show
 *
 * The order is load-bearing, not cosmetic. The diagnostics half is UNTRUSTED
 * text: an app can print anything it likes to stderr, including the exact
 * phrases the platform uses to state whether an app is still up, and it can
 * influence what the runtime quotes back (a bad entrypoint is echoed in the
 * fault). Keeping that text strictly LAST means the head is the platform's own
 * words. Interpolating it into the middle of a reason would reopen that hole.
 *
 * 🔴 NO SURFACE CLASSIFIES A FAILURE ON THIS STRING — the verdict is the
 * structured `reasonCode` (`VibeFailureReasonCode`). The head is not a safe
 * thing to match on in general: a BUILD failure's reason is the runner's raw
 * output, never composed here, so it has no separator and its "head" is the
 * tenant's whole build log. Read the head only after a code has proven the
 * reason was composed by one of this file's callers.
 */

/**
 * Separates the platform's named reason from the diagnostics beneath it. A
 * blank line: the first one in a reason is always this boundary, because the
 * named half is composed here and never contains one.
 */
export const VIBE_FAILURE_DIAGNOSTICS_SEPARATOR = "\n\n";

/**
 * Compose a customer-visible failure reason: the platform's named reason, with
 * the app's own diagnostics underneath when there are any.
 *
 * `named` is never optional — every failure gets a name, so the reason is
 * always classifiable and never degrades to a bare, meaningless string.
 */
export function composeVibeFailureReason(named: string, diagnostics?: string | null): string {
  if (diagnostics === undefined || diagnostics === null || diagnostics.trim() === "") {
    return named;
  }
  return `${named}${VIBE_FAILURE_DIAGNOSTICS_SEPARATOR}${diagnostics}`;
}

/**
 * The platform-authored head of a failure reason — everything before the first
 * blank line. This is the ONLY half safe to match assertions against; see the
 * file header. A reason with no diagnostics is entirely its own head.
 */
export function vibeFailureReasonHead(reason: string): string {
  const boundary = reason.indexOf(VIBE_FAILURE_DIAGNOSTICS_SEPARATOR);
  return boundary === -1 ? reason : reason.slice(0, boundary);
}
